import { useEffect, useRef } from 'react';

/**
 * useKeyboardShortcuts hook
 * Provides global keyboard shortcuts for chat interface
 *
 * Shortcuts:
 * - Cmd/Ctrl + K: Create new chat thread
 * - Esc: Stop current execution
 * - /: Focus on input (only when not already typing)
 *
 * @param {Object} options
 * @param {Function} options.onNewChat - Callback for creating new chat (Cmd/Ctrl + K fallback)
 * @param {Function} options.onSearch - Callback for opening search (Cmd/Ctrl + K preferred)
 * @param {Function} options.onStop - Callback for stopping execution (Esc)
 * @param {Function} options.onFocusInput - Callback for focusing input (/)
 * @param {boolean} options.canStop - Whether stop action is currently available
 * @param {boolean} options.enabled - Whether shortcuts are enabled (default: true)
 */
export const useKeyboardShortcuts = ({
  onNewChat,
  onSearch,
  onStop,
  onFocusInput,
  canStop = false,
  enabled = true
}) => {
  // ✅ FIX: Use refs to store current callback values
  // This prevents constant re-subscriptions when callbacks change identity
  const handlersRef = useRef({ onNewChat, onSearch, onStop, onFocusInput, canStop });

  // Update ref when values change (doesn't trigger re-subscription)
  useEffect(() => {
    handlersRef.current = { onNewChat, onSearch, onStop, onFocusInput, canStop };
  }, [onNewChat, onSearch, onStop, onFocusInput, canStop]);

  // ✅ FIX: Single stable event listener that reads from ref
  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (event) => {
      const { onNewChat, onSearch, onStop, onFocusInput, canStop } = handlersRef.current;

      // Cmd/Ctrl + K: Open search (preferred) or new chat (fallback)
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        if (onSearch) {
          onSearch();
        } else if (onNewChat) {
          onNewChat();
        }
        return;
      }

      // Esc: Stop execution (only if agent is active)
      if (event.key === 'Escape') {
        event.preventDefault();
        if (canStop && onStop) {
          onStop();
        }
        return;
      }

      // /: Focus input (only if not already focused on an input/textarea)
      if (event.key === '/') {
        const activeElement = document.activeElement;
        const isTyping =
          activeElement?.tagName === 'INPUT' ||
          activeElement?.tagName === 'TEXTAREA' ||
          activeElement?.isContentEditable;

        if (!isTyping) {
          event.preventDefault();
          if (onFocusInput) {
            onFocusInput();
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [enabled]); // ✅ Only re-subscribe when enabled changes
};
